import { useState, useCallback, useEffect } from 'react';
import { useGetDebtByIdQuery, useUpdateDebtMutation } from 'services/debt.service';
import { DebtStatusCode, PaymentType } from 'models';
import { Dayjs } from 'dayjs';
import {
  useCreateAttachmentMutation,
  useGetAllAttachmentTypesQuery,
  useGetAllTransactionMethodsQuery,
  useGetStatusQuery,
  useLazyGetTransactionsByRefIdQuery,
  useCreateDebtDocumentMutation,
} from 'services';
import { showAlertNotification } from 'components/common';
import { Attachment } from 'models/Attachment';
import { FileData } from 'types';
import { formatPrice } from 'utils';
import { Transaction } from 'models/Transaction';

const useDebtDetailOperations = ({ debtId }: { debtId: string }) => {
  const { data, isLoading, refetch } = useGetDebtByIdQuery(
    {
      id: debtId,
      query: {
        include: 'status|debtDueExtensions|debtAttachments.attachment',
        includeRef: 'SaleOrders.saleOrderItems|InventoryDocuments.inventoryDocumentItems',
      },
    },
    {
      skip: !debtId,
    },
  );
  const [updateDebt, { isLoading: isLoadingUpdateDebt }] = useUpdateDebtMutation();
  const { data: transactionMethodsData } = useGetAllTransactionMethodsQuery(
    {
      query: {
        size: 'unlimited',
      },
    },
    {
      skip: !debtId,
    },
  );
  const [getTransactionByRefId] = useLazyGetTransactionsByRefIdQuery();
  const [createDebtDocument, { isLoading: isLoadingCreateDebtDocument }] = useCreateDebtDocumentMutation();
  const { data: statusData } = useGetStatusQuery(
    {
      query: {
        filter: 'type:eq:debt',
      },
    },
    {
      skip: !debtId,
    },
  );
  // Local state for form data
  const [paymentMethod, setPaymentMethod] = useState<PaymentType>(PaymentType.FULL_PAYMENT);
  const [scheduleDate, setScheduleDate] = useState<Dayjs | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [filesData, setFilesData] = useState<FileData[]>([]);
  const [createAttachment] = useCreateAttachmentMutation();
  const [transactionAttachments, setTransactionAttachments] = useState<FileData[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  const { data: attachmentTypes } = useGetAllAttachmentTypesQuery({
    query: {
      size: 'unlimited',
    },
  });
  useEffect(() => {
    if (data?.data?.refId) {
      getTransactionByRefId({
        refId: data.data.refId,
        query: {
          size: 'unlimited',
        },
      }).then((result) => {
        const transactionData = result.data?.data.rows || [];
        if (transactionData) {
          setTransactions(transactionData);
        }
      });
    }
    if (data?.data?.debtAttachments) {
      setAttachments(data.data.debtAttachments.map((attachment) => attachment.attachment as Attachment));
      setFilesData(
        data.data.debtAttachments.map((attachment, index) => ({
          id: attachment.id,
          filePath: attachment.attachment?.filePath || '',
          order: index + 1,
          name: attachment.attachment?.name || '',
        })),
      );
    }
  }, [data]);
  const handleConfirm = useCallback((): boolean => {
    if (paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX || paymentMethod === PaymentType.NEGOTIATED_PAYMENT) {
      if (!scheduleDate) {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi xác nhận thanh toán',
          message: 'Vui lòng chọn ngày trả phần còn lại.',
        });
        return false;
      }
      if (paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX) {
        if (!paymentAmount) {
          showAlertNotification({
            type: 'error',
            title: 'Lỗi xác nhận thanh toán',
            message: 'Vui lòng nhập số tiền trả thêm.',
          });
          return false;
        } else if (paymentAmount > (data?.data.remainingAmount || 0)) {
          showAlertNotification({
            type: 'error',
            title: 'Lỗi xác nhận thanh toán',
            message: 'Số tiền trả thêm không được lớn hơn số tiền còn lại.',
          });
          return false;
        }
      }
    }
    return true;
  }, [paymentMethod, scheduleDate, paymentAmount]);
  // Handle confirm payment
  const handleConfirmPayment = useCallback(async (): Promise<void> => {
    try {
      if (paymentMethod === PaymentType.FULL_PAYMENT || paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX) {
        const playload = {
          id: debtId,
          transaction: {
            transactionMethodId: transactionMethodsData?.data?.rows.find((method) => method.code === 'CASH')?.id || '',
            transactionAmount:
              paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX ? paymentAmount || 0 : data?.data.remainingAmount || 0,
            transactionAttachments: transactionAttachments.map((file) => ({
              attachmentId: file.id || '',
            })),
          },
          statusId:
            paymentMethod === PaymentType.FULL_PAYMENT || paymentAmount === data?.data.remainingAmount
              ? statusData?.data?.find((status) => status.code === DebtStatusCode.COMPLETED)?.id
              : undefined,
          debtDueExtension:
            scheduleDate &&
            paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX &&
            paymentAmount !== data?.data.remainingAmount
              ? {
                  extraFee: 0,
                  oldDeadline: data?.data.deadline ? data?.data.deadline.toString() : new Date().toISOString(),
                  newDeadline: scheduleDate?.toISOString() || '',
                }
              : undefined,
          deadline:
            scheduleDate &&
            paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX &&
            paymentAmount !== data?.data.remainingAmount
              ? scheduleDate?.toISOString() || ''
              : data?.data.deadline?.toString() || '',
        };
        await updateDebt(playload).unwrap();
      }
      if (paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX) {
        showAlertNotification({
          type: 'success',
          title: 'Thanh toán thành công',
          message: 'Thanh toán thêm ' + formatPrice(paymentAmount || 0) + ' VNĐ và gia hạn thời gian trả thành công.',
        });
      }
      if (paymentMethod === PaymentType.FULL_PAYMENT) {
        showAlertNotification({
          type: 'success',
          title: 'Thanh toán thành công',
          message: 'Thanh toán toàn bộ công nợ thành công.',
        });
      }
      refetch();
    } catch (error) {
      console.log('Error confirming payment:', error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi xác nhận thanh toán',
        message: 'Đã xảy ra lỗi khi xác nhận thanh toán. Vui lòng thử lại.',
      });
    }
  }, [
    debtId,
    paymentMethod,
    scheduleDate,
    filesData,
    refetch,
    paymentAmount,
    transactionAttachments,
    attachments,
    data,
  ]);
  const handleAddOverdue = useCallback(async (): Promise<void> => {
    try {
      const payload = {
        id: debtId,
        debtDueExtension: {
          extraFee: 0,
          oldDeadline: data?.data.deadline ? data?.data.deadline.toString() : new Date().toISOString(),
          newDeadline: scheduleDate?.toISOString() || new Date().toISOString(),
        },
        deadline: scheduleDate
          ? scheduleDate?.toISOString() || new Date().toISOString()
          : data?.data.deadline?.toString() || new Date().toISOString(),
      };
      await updateDebt(payload).unwrap();

      showAlertNotification({
        type: 'success',
        title: 'Thêm quá hạn thành công',
        message: 'Công nợ đã được thêm quá hạn thành công',
      });
      refetch();
    } catch (error) {
      console.error('Error adding overdue:', error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi thêm quá hạn',
        message: 'Đã xảy ra lỗi khi thêm quá hạn. Vui lòng thử lại.',
      });
    }
  }, [debtId, data, updateDebt, refetch, scheduleDate]);
  // Handle print
  const handlePrint = useCallback(async () => {
    try {
      const response = await createDebtDocument({ id: debtId });
      const documentUrl = response?.data?.data?.fileUrl;
      if (!documentUrl) return;

      const res = await fetch(documentUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      };
    } catch (error) {
      console.log(error);
      if ((error as Error)?.message) {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi in hóa đơn',
          message: (error as Error)?.message,
        });
      } else {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi in hóa đơn',
          message: 'Đã xảy ra lỗi khi in hóa đơn. Vui lòng thử lại.',
        });
      }
    }
  }, [debtId]);
  const handleUpload = useCallback(
    async (files: File[]): Promise<void> => {
      try {
        for (const file of files) {
          const attachment = await createAttachment({
            name: 'Transaction Attachment ' + new Date().toISOString(),
            filePath: file.name,
            attachmentTypeId: attachmentTypes?.data?.rows.filter((type) => type.code === 'transaction')[0]?.id || '',
          }).unwrap();
          setTransactionAttachments((prev) => [
            ...prev,
            {
              id: attachment.data.id,
              filePath: attachment.data.filePath,
              order: prev.length + 1,
              name: file.name,
            },
          ]);
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi tải lên tệp',
          message: 'Đã xảy ra lỗi khi tải lên tệp. Vui lòng thử lại.',
        });
      }
    },
    [debtId, createAttachment, attachmentTypes, refetch, transactionAttachments],
  );
  const handleOnNewFileUpload = useCallback(
    async (filePath: string, fileName: string) => {
      try {
        // create attachment
        const attachment = await createAttachment({
          name: 'Debt Attachment ' + new Date().toISOString() + ' ' + fileName,
          filePath,
          attachmentTypeId: attachmentTypes?.data?.rows.filter((type) => type.code === 'transaction')[0]?.id || '',
        }).unwrap();
        await updateDebt({
          id: debtId,
          debtAttachments: [
            ...attachments.map((file) => ({
              attachmentId: file.id || '',
            })),
            { attachmentId: attachment.data.id || '' },
          ],
        }).unwrap();
        setAttachments((prev) => [...prev, attachment.data as Attachment]);
        showAlertNotification({
          type: 'success',
          title: 'Tải lên chứng từ thành công',
          message: 'Chứng từ đã được tải lên thành công.',
        });
      } catch (error) {
        console.error('Error uploading file:', error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi tải lên chứng từ',
          message: 'Đã xảy ra lỗi khi tải lên chứng từ. Vui lòng thử lại.',
        });
      }
    },
    [createAttachment, attachmentTypes, data, attachments, updateDebt],
  );
  const handleOnRemoveFile = useCallback(
    async (filePath: string) => {
      try {
        await updateDebt({
          id: debtId,
          debtAttachments: attachments
            .filter((attachment) => attachment.filePath !== filePath)
            .map((attachment) => ({
              attachmentId: attachment.id,
            })),
        }).unwrap();
        setAttachments((prev) => prev.filter((attachment) => attachment.filePath !== filePath));
        showAlertNotification({
          type: 'success',
          title: 'Xóa chứng từ thành công',
          message: 'Chứng từ đã được xóa thành công.',
        });
      } catch (error) {
        console.error('Error removing file:', error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi khi xóa tệp',
          message: 'Đã xảy ra lỗi khi xóa tệp. Vui lòng thử lại.',
        });
      }
    },
    [data, attachments, updateDebt],
  );
  return {
    debt: data?.data,
    purchaseOrder: data?.data?.InventoryDocuments?.[0] || null,
    saleOrder: data?.data?.SaleOrders?.[0] || null,
    transactions: transactions || [],
    isLoading: isLoading,
    isLoadingCreateDebtDocument,
    paymentMethod,
    scheduleDate,
    paymentAmount,
    filesData,
    setPaymentMethod,
    setScheduleDate,
    setFilesData,
    setPaymentAmount,
    handleConfirmPayment,
    handlePrint,
    // handleUpdateDebt,
    handleUpload,
    handleAddOverdue,
    handleConfirm,
    handleOnNewFileUpload,
    handleOnRemoveFile,
    setTransactionAttachments,
    isUpdating: isLoadingUpdateDebt,
  };
};

export default useDebtDetailOperations;
